<script setup
        lang="ts"
>
import type { SelectedFilters, UnifiedFilter } from '@/js/vue/filter/types.ts';
import { classname } from '@/js/utils.ts';
import AppliedFilterChip from '@/js/vue/filter/applied-filter-chip.vue';
import { computed } from 'vue';

const props = defineProps<{
  filters: UnifiedFilter[]
  appliedFilters: SelectedFilters
}>();

const emits = defineEmits<{
  update: (filters: SelectedFilters) => void
}>();

const displayFilters = computed(() => {
  return props.filters.map((filter) => {
    const appliedFilter = props.appliedFilters[filter.key];
    if (!appliedFilter) {
      return null;
    }

    if (filter.type === 'range') {
      return {
        ...filter,
        value: appliedFilter,
      };
    }

    return {
      ...filter,
      value: filter.items.filter(item => appliedFilter.includes(item.id)),
    };
  }).filter(item => item !== null) as (UnifiedFilter & { value: unknown })[];
});

function removeFilter(filter: UnifiedFilter) {
  const filters = { ...props.appliedFilters };
  delete filters[filter.key];
  emits('update', filters);
}

function unsetFilterValue(filter: UnifiedFilter, value: unknown) {
  const filters = { ...props.appliedFilters };
  filters[filter.key] = filter.value.filter((item: { id: unknown }) => item.id !== value).map(item => item.id);
  emits('update', filters);
}

const cn = classname('applied-filters');
</script>

<template>
  <div v-if="displayFilters.length > 0" :class="cn()">
    <div
      v-for="filter in displayFilters"
      :key="filter.key"
      :class="cn('item')"
    >
      <span
        :class="cn('name')"
        v-html="filter.name"
      />
      <AppliedFilterChip
        v-if="filter.type === 'range'"
        @click="removeFilter(filter)"
      >
        {{ filter.value.from }} - {{ filter.value.to }}
      </AppliedFilterChip>
      <template v-else-if="filter.type === 'variants'">
        <AppliedFilterChip
          v-for="(value, index) in filter.value"
          :key="index"
          @click="unsetFilterValue(filter, value.id)"
        >
          {{ value.value }}
        </AppliedFilterChip>
      </template>
    </div>
  </div>
</template>

<style scoped
       lang="scss"
>
.applied-filters {
  display: grid;
  gap: 20px;
  padding: 20px;
  border-bottom: 1px solid var(--border-color);

  &__item {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 8px;
  }
}
</style>
